import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { venueManagement, venues as venuesApi } from '../services/api';
import VenueEditModal from './VenueEditModal';
import {
  BuildingOfficeIcon,
  MapPinIcon,
  DevicePhoneMobileIcon,
  UserGroupIcon,
  PlusIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';

interface Venue {
  id?: string; // venues.list() returns 'id'
  venue_id?: string; // venueManagement returns 'venue_id'
  name?: string; // venues.list() returns 'name'
  venue_name?: string; // venueManagement returns 'venue_name'
  address?: string;
  city?: string;
  country?: string;
  timezone?: string;
  contact_email?: string;
  contact_phone?: string;
  access_level?: string;
  device_count?: string | number;
  online_devices?: string | number;
  compliance_status?: string;
  active_violations?: string | number;
}

const Venues: React.FC = () => {
  const { user } = useAuth();
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingVenue, setEditingVenue] = useState<any | null>(null);

  useEffect(() => {
    fetchVenues();
  }, []);

  const fetchVenues = async () => {
    try {
      // Admin and engineer use the venues API to see all venues
      // Other roles use venueManagement to see only their venues
      let response;
      if (user?.role === 'admin' || user?.role === 'engineer') {
        response = await venuesApi.list();
      } else {
        response = await venueManagement.getMyVenues();
      }
      setVenues(response.data);
    } catch (error) {
      console.error('Error fetching venues:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setEditingVenue(null);
    setModalMode('create');
    setModalOpen(true);
  };

  const handleOpenEditModal = (venue: Venue) => {
    const venueData = {
      ...venue,
      id: venue.id || venue.venue_id,
      name: venue.name || venue.venue_name || '',
      address: venue.address || '',
      city: venue.city || '',
      country: venue.country || '',
      timezone: venue.timezone || 'America/Los_Angeles',
      contact_email: venue.contact_email || '',
      contact_phone: venue.contact_phone || ''
    };
    setEditingVenue(venueData);
    setModalMode('edit');
    setModalOpen(true);
  };

  const handleSaveVenue = async (venueData: any) => {
    try {
      if (modalMode === 'create') {
        await venueManagement.createVenue(venueData);
      } else if (editingVenue) {
        const venueId = editingVenue.id || editingVenue.venue_id;
        await venuesApi.update(venueId!, venueData);
      }
      await fetchVenues();
      setModalOpen(false);
    } catch (error) {
      console.error('Error saving venue:', error);
      throw error;
    }
  };

  const getComplianceIcon = (status: string) => {
    switch (status) {
      case 'compliant':
        return <CheckCircleIcon className="h-6 w-6 text-green-500" />;
      case 'warning':
        return <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500" />;
      case 'violation':
        return <XCircleIcon className="h-6 w-6 text-red-500" />;
      default:
        return <XCircleIcon className="h-6 w-6 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-300 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="h-6 bg-gray-300 rounded w-3/4 mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Venues</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage your venues and their compliance status
          </p>
        </div>
        {(user?.role === 'engineer' || user?.role === 'admin') && (
          <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
            <button
              onClick={handleOpenCreateModal}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Venue
            </button>
          </div>
        )}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {venues.map((venue) => {
          // Handle both API response formats
          const venueId = venue.id || venue.venue_id;
          const venueName = venue.name || venue.venue_name;
          const deviceCount = venue.device_count || 0;
          const activeViolations = venue.active_violations || 0;
          
          return (
            <div
              key={venueId}
              className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg hover:shadow-lg transition-shadow"
            >
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <BuildingOfficeIcon className="h-8 w-8 text-gray-400" />
                  <div className="flex items-center space-x-2">
                    {(user?.role === 'engineer' || user?.role === 'admin') && (
                      <button
                        onClick={() => handleOpenEditModal(venue)}
                        className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700"
                        title="Edit venue"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                    )}
                    {getComplianceIcon(venue.compliance_status || 'compliant')}
                  </div>
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {venueName}
                </h3>
                
                {(venue.address || venue.city || venue.country) && (
                  <div className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    <div className="flex items-start">
                      <MapPinIcon className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0" />
                      <div>
                        {venue.address && <div>{venue.address}</div>}
                        <div>
                          {[venue.city, venue.country].filter(Boolean).join(', ')}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <DevicePhoneMobileIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 mr-1" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {deviceCount} devices
                    </span>
                  </div>
                  {venue.access_level && (
                    <div className="flex items-center">
                      <UserGroupIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 mr-1" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {venue.access_level}
                      </span>
                    </div>
                  )}
                </div>

                {Number(activeViolations) > 0 && (
                  <div className="mt-3 p-2 bg-red-50 rounded">
                    <p className="text-sm text-red-800">
                      {activeViolations} active violation{Number(activeViolations) !== 1 ? 's' : ''}
                    </p>
                  </div>
                )}

                <div className="mt-4 flex space-x-2">
                  <button
                    onClick={() => window.location.href = `/venues/${venueId}`}
                    className="flex-1 px-3 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 rounded hover:bg-indigo-100"
                  >
                    View Details
                  </button>
                  <button
                    onClick={() => window.location.href = `/venues/${venueId}/add-device`}
                    className="flex-1 px-3 py-2 text-sm font-medium text-green-600 bg-green-50 rounded hover:bg-green-100"
                  >
                    Add Device
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {venues.length === 0 && (
          <div className="col-span-full text-center py-12">
            <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No venues</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {(user?.role === 'engineer' || user?.role === 'admin')
                ? "Get started by creating a new venue."
                : "You don't have access to any venues yet."}
            </p>
            {(user?.role === 'engineer' || user?.role === 'admin') && (
              <div className="mt-6">
                <button
                  onClick={handleOpenCreateModal}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Add Venue
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Venue Create/Edit Modal */}
      <VenueEditModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSaveVenue}
        venue={editingVenue}
        mode={modalMode}
      />
    </div>
  );
};

export default Venues;